// Horizontal step strip shown above flows and (optionally) above
// single-endpoint playgrounds — gives developers a sense of where this call
// sits inside the bigger pipeline.
//
// Renders a row of pill-shaped step labels separated by chevrons, with the
// active step highlighted in brand purple and completed steps in a softer
// success green.

import type { ReactNode } from 'react';

export interface SequenceStep {
  id: string;
  label: string;
  /** Compact one-line description shown under the label on hover (title attr). */
  hint?: string;
  /** Optional callback for clicks (used by flows to jump steps). */
  onClick?: () => void;
}

export type StepStatus = 'done' | 'active' | 'pending';

interface Props {
  steps: SequenceStep[];
  /** ID of the currently active step. Steps before it render as `done`. */
  activeId: string;
  /** Optional override: explicit per-step status. Wins over the `activeId`-based default. */
  statusOverrides?: Record<string, StepStatus>;
  /** Optional label printed left of the strip (e.g. "Flow"). */
  eyebrow?: ReactNode;
  className?: string;
}

export function SequenceDiagram({ steps, activeId, statusOverrides, eyebrow, className }: Props) {
  const activeIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === activeId)
  );

  return (
    <div
      className={`lp-flex lp-items-center lp-gap-2 lp-flex-wrap ${className || ''}`}
      role="list"
      aria-label="Pipeline sequence"
    >
      {eyebrow && (
        <span
          className="lp-text-[10px] lp-font-bold lp-uppercase lp-mr-1"
          style={{ letterSpacing: '0.12em', color: '#6256ff' }}
        >
          {eyebrow}
        </span>
      )}
      {steps.map((step, i) => {
        const status: StepStatus =
          statusOverrides?.[step.id] ?? (i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'pending');
        const isLast = i === steps.length - 1;
        return (
          <div key={step.id} className="lp-flex lp-items-center lp-gap-2" role="listitem">
            <StepPill step={step} index={i} status={status} />
            {!isLast && (
              <span aria-hidden style={{ color: '#cbd5e1', fontSize: 10 }}>
                ▸
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepPill({
  step,
  index,
  status,
}: {
  step: SequenceStep;
  index: number;
  status: StepStatus;
}) {
  const baseStyle: React.CSSProperties = {
    fontSize: 11.5,
    fontWeight: 600,
    padding: '5px 10px 5px 6px',
    borderRadius: 9999,
    border: '1px solid',
    transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
    cursor: step.onClick ? 'pointer' : 'default',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const statusStyle: React.CSSProperties =
    status === 'active'
      ? {
          background: 'linear-gradient(135deg, #6256ff, #7c3aed)',
          borderColor: 'transparent',
          color: '#ffffff',
          boxShadow: '0 6px 14px rgba(98,86,255,0.28)',
        }
      : status === 'done'
        ? {
            background: '#ecfdf5',
            borderColor: '#a7f3d0',
            color: '#047857',
          }
        : {
            background: '#ffffff',
            borderColor: '#e5e7eb',
            color: '#94a3b8',
          };

  const dotStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    borderRadius: 9999,
    fontSize: 10,
    fontWeight: 800,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      status === 'active'
        ? 'rgba(255,255,255,0.22)'
        : status === 'done'
          ? '#10b981'
          : '#f1f5f9',
    color:
      status === 'active' ? '#ffffff' : status === 'done' ? '#ffffff' : '#94a3b8',
  };

  const Tag = step.onClick ? 'button' : 'span';
  return (
    <Tag
      onClick={step.onClick}
      title={step.hint || step.label}
      style={{ ...baseStyle, ...statusStyle }}
      className="lp-whitespace-nowrap"
    >
      <span style={dotStyle} aria-hidden>
        {status === 'done' ? (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          index + 1
        )}
      </span>
      {step.label}
    </Tag>
  );
}
